import {
  useCreateAccountForCareseekerMutation,
  useUpdateAccountRoleStationMapping,
} from "apollo/hooks/mutations";
import {
  ACCOUNT_ROLE_CLINICAL_DOCUMENTATION,
  ACCOUNT_ROLE_PREDICT,
  TRACK_EVENTS,
} from "core/consts";
import { hasCareseekerRole } from "core/model/accounts";
import { Account, Careseeker } from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useAddAccountToSealdGroup } from "dsl/hooks/useAddAccountToSealdGroup";
import { useClinicalDocumentationAssignmentMutation } from "dsl/organisms/Accounts/useClinicalDocumentationAssignment";
import { AccountFormSubmittedValues } from "dsl/organisms/Accounts/useCreateAccountWithRoles";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useTracking } from "react-tracking";
import { useLocale, useTranslations } from "translations";
import { AccountDialogState, getTimestamp } from "./useUserManagementFeature";

export function useCreateAccountForCareseeker({
  careseeker,
  setAccountDialog,
}: {
  careseeker: Careseeker | undefined;
  setAccountDialog: Dispatch<SetStateAction<AccountDialogState>>;
}) {
  const locale = useLocale();
  const translations = useTranslations();
  const toast = useToast();
  const { trackEvent } = useTracking();
  const [addAccountToSealdGroup] = useAddAccountToSealdGroup();
  const [updateAccountRoleStationMapping] =
    useUpdateAccountRoleStationMapping();
  const [updateClinicalDocumentationAssignment] =
    useClinicalDocumentationAssignmentMutation();

  const [createAccount, createProgress, reset] =
    useCreateAccountForCareseekerMutation({
      careseekerId: careseeker?.id || -1,
      onCompleted: () => {
        toast({ color: "success", message: translations.general.success });
        reset();
        setAccountDialog({ step: null });
      },
    });

  const handleCreateAccount = useCallback(
    async (data: AccountFormSubmittedValues) => {
      const {
        dischargeLetterFileTypes,
        predictStations,
        send_activation_email: _,
        ...account
      } = data;

      // handler only accepts careseeker_roles
      delete account.roles.admin_roles;
      delete account.roles.careprovider_roles;

      // Create Account
      trackEvent({ name: TRACK_EVENTS.UM_CREATE_MODAL_SUBMIT });
      const response = await createAccount(account, {
        timestamp: getTimestamp(locale),
      });

      if (response?.data) {
        await addAccountToSealdGroup({
          account: response.data.account,
          sealdId: careseeker?.seald_encryption_context?.seald_id,
        });
        const hasPredictRole = hasCareseekerRole(
          account as Account,
          careseeker?.id,
          ACCOUNT_ROLE_PREDICT,
        );
        const hasClinicalDocumentationRole = hasCareseekerRole(
          account as Account,
          careseeker?.id,
          ACCOUNT_ROLE_CLINICAL_DOCUMENTATION,
        );

        if (
          hasPredictRole &&
          Array.isArray(predictStations) &&
          predictStations.length > 0 &&
          response.data.account.id
        ) {
          try {
            await updateAccountRoleStationMapping({
              account_id: response.data.account.id,
              careseeker_id: careseeker?.id,
              role_value: ACCOUNT_ROLE_PREDICT,
              station_ids: predictStations,
            });
          } catch (error) {
            console.error("Error associating stations with account:", error);
            toast({
              color: "danger",
              message: translations.acp.acpAccountProfile.predictStationError,
            });
          }
        }

        if (
          hasClinicalDocumentationRole &&
          Array.isArray(dischargeLetterFileTypes) &&
          dischargeLetterFileTypes.length > 0 &&
          careseeker?.id &&
          response.data.account.id
        ) {
          try {
            await updateClinicalDocumentationAssignment({
              accountId: response.data.account.id,
              careseekerId: careseeker?.id,
              fileTypes: dischargeLetterFileTypes,
            });
          } catch (error) {
            console.error(
              "Error associating discharge letter file types with account:",
              error,
            );
            toast({
              color: "danger",
              message: translations.general.error,
            });
          }
        }
      }
    },
    [
      addAccountToSealdGroup,
      careseeker?.id,
      careseeker?.seald_encryption_context?.seald_id,
      createAccount,
      locale,
      toast,
      trackEvent,
      translations.acp.acpAccountProfile.predictStationError,
      updateAccountRoleStationMapping,
      updateClinicalDocumentationAssignment,
    ],
  );

  return { createProgress, handleCreateAccount };
}
